import { generate } from '../../../core/js/utils/componentUtils';
import { getDefaultOptions } from '../../../core/js/utils/a15componentUtils';
import { favoriteClicked } from '../utils/favoritesUtils';
import { launchAsyncQuickshop } from '@product/js/utils/asyncLaunchers';
import { defaultQuickshopOptions } from '../utils/defaultQuickshopOptions';
import {
    setProductTileSwatchImage,
    setProductTileSwatchUrl,
    setProductTileActiveSwatch
} from '../../../product/js/utils/tileUtils';

import {
    productTraySwiperInit,
    specialtyShopTraySwiperInit
} from '../../../product/js/utils/swiperInits';

/**
 *
 * Landing page locations are not standardized in the store.
 *
 * That means category stores the landing page data in `category.landingPage`,
 * Homepage stores the data in `homepage.landingPage`, etc.
 *
 * So counter this, a `feature` must be passed into the overrides ('category', 'homepage', etc)
 *
 * Furthermore, once the data for the trays is pulled down the data will be put into
 * `core.productTrays`
 *
 */

function launchQuickshopFromTile(componentIndex, store, event) {
    event.preventDefault();
    const productIndex = $(event.target).parents('.js-product-tile')
        .attr('data-product-index');
    const records = _.map(_.get(this, `productTrays.${componentIndex}.records`, []), 'allMeta.tile');
    launchAsyncQuickshop(store, defaultQuickshopOptions, event, records, productIndex);
}

/**
 * Determine if the module being passed in contains a product grid component.
 * @param {object} module The module we are exploring.
 * @return {boolean} Boolean to determine if we have a product grid component in our module.
 */
function contentfulModuleContainsProductTrays(module) {
    const moduleContentType = _.get(module, 'fields.contentType');
    const componentContentTypes = [];

    if(moduleContentType === 'specialtyShop' || moduleContentType === 'categoryProductTray') {
        return true;
    }

    const components = _.get(module, 'fields.components');

    if (components) {
        components.forEach((c, index) => {
            const componentContentType = _.get(c, 'sys.contentType.sys.id');
            componentContentTypes.push(componentContentType === 'componentProductGrid');
        });
    }

    return componentContentTypes.includes(true);
}

/**
 * Pull out all product trays from the landing page
 *
 * returns the entry.fields.tray object
 * @param {object} store Redux store
 * @param {string} feature current feature, 'homepage', 'category', etc
 * @return {array} list of product trays
 */
export function getProductTrays(store, feature) {
    // UW-14862 - This is needed so product trays will load on help pages correctly.
    // Since the landing page modules are stored in helpContent and not landingPage.
    const featureModulesSource = feature === 'help' ? `${feature}.helpContent` : `${feature}.landingPage`;
    const landingPageModules = _.get(store.getState(), `${featureModulesSource}.fields.modules`, []);

    const productTrays = [];
    const trayModules = _.filter(landingPageModules, (m) => {
        return contentfulModuleContainsProductTrays(m);
    });
    let productTrayComponents = [];

    for(let i = 0; i < trayModules.length; i++) {
        const moduleComponents = _.get(trayModules[i], 'fields.components', []);

        // NOTE We are flatening the tray modules here, sometimes
        // our modules have multiple components. Sometimes,
        // it's just one. Regardless, let's remove the original
        // object from the tray modules for later concatenation.
        if(moduleComponents.length) {
            trayModules.splice(i, i + 1);
            i = i - 1;
            productTrayComponents = productTrayComponents.concat(...moduleComponents);
        }
    }

    if (productTrayComponents.length > 0) {
        let componentProductTray;

        for (let i = 0; i < productTrayComponents.length; i++) {
            componentProductTray = _.get(productTrayComponents, `[${i}].fields.tray`);

            if (componentProductTray) {
                productTrays.push(componentProductTray);
            }
        }
    }
    return [
        ..._.map(trayModules, (m) => {
            return _.get(m, 'fields.components.1.fields.tray') || _.get(m, 'fields.tray');
        }),
        ...productTrays,
    ];
}

export function attachEvents(store, componentIndex) {
    const productTrayContent = this.scopedSelector();
    const quickshopSelector = `${productTrayContent}[data-index=${componentIndex}] .js-product-tile-control-quickshop`;
    $(document).on(this.scopedEvent('touchend click'), quickshopSelector, (event) => {
        launchQuickshopFromTile.call(this, componentIndex, store, event);
    });

    $(document).on(this.scopedEvent('touchend click'), this.scopedSelector('.js-swatch-link-category'), (e) => {
        e.preventDefault();
        setProductTileSwatchImage(store, e);
        setProductTileSwatchUrl(e);
        setProductTileActiveSwatch(e);
    });

    /**
     * Rich Relevance Click Tracking
     */
    $(document).on(this.scopedEvent('touchend click'), this.scopedSelector('a[data-rr-click-tracking]'), (e) => {
        $.ajax($(e.currentTarget).data('rr-click-tracking'));
    });

    // eslint-disable-next-line max-len
    $(document).on(this.scopedEvent('click touchend'), `div[data-index=${componentIndex}] .js-favorite-toggle`, { selector: `div[data-index=${componentIndex}]` }, (e) => favoriteClicked(store, e));
}

function afterConstruction(store) {
    const slidesPerView = 4;

    // Specialty shop product trays need their own config, and must be
    // initialized separate from regular product trays.  To handle this, we
    // took the approach of making their scoping selectors extra specific, then
    // calling BOTH inits for every product tray on the page.  Since the first
    // line inside the init detects if that selector is available, we can do
    // this safely without having accidental double initializations.

    for (let i = 0; i < this.multiple(); i++) {
        const productTrayScopingSelector = `.js-slider-product-tray div[data-index="${i}"]`;
        const specialtyShopTrayScopingSelector = `.js-slider-specialty-shop-tray div[data-index="${i}"]`;

        productTraySwiperInit(productTrayScopingSelector, slidesPerView);
        specialtyShopTraySwiperInit(specialtyShopTrayScopingSelector, slidesPerView);
    }
}

function afterRebind(store) {
    this.onStateChange(store);
}

/**
 * Initializing component should pass in an override for 'feature'
 * @param {object} store Redux store
 * @param {object} overrides overrides
 * @returns {*} new component
 */
export function init(store, overrides = {}) {
    const config = {
        templateLocation: '.dom-product-tray-content',
        watch: [],
        name: 'productTrayComponent',
        attachEvents: attachEvents,
        afterConstruction: afterConstruction,
        afterRebind: afterRebind,
        feature: 'category'
    };

    const feature = overrides.feature || config.feature;

    overrides.productTrays = getProductTrays(store, feature);
    config.multiple = () => {
        return overrides.productTrays.length;
    };

    return generate(config, store, overrides, getDefaultOptions(store));
}
